import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Image, Platform, ActivityIndicator, TextInput, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../../../styles/variablesCss';
import { BACKEND_URL } from '../../../../Config/Const';
import { useCart } from '../../../../context/CartContext';
import { useUser } from '../../../../context/UserContext';
import Storage from '../../../../utils/Storage';
import Login from '../../Header/AdminUsuario/Login';
import FeedbackModal from '../../../Common/FeedbackModal';
import { formatPrecio } from '../../../../utils/formatPrice';

/**
 * Modal para mostrar productos combinables con el producto actual
 * @param {Object} props - Component props
 * @param {boolean} visible - Si el modal está visible
 * @param {function} onClose - Función para cerrar el modal
 * @param {Object} combo - Información del combo combinable
 * @param {Object} producto - Información del producto principal
 */
const Combinables = ({ visible, onClose, combo, producto }) => {
  // Función de sanitización para evitar renderizar valores inválidos
  const safeText = (value, defaultValue = '') => {
    if (!value || value === '.' || value === null || value === undefined) return defaultValue;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '.') return defaultValue;
    return stringValue;
  };

  // Estados para manejar la carga de productos combinables
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Estado para manejar las cantidades de cada producto
  const [cantidades, setCantidades] = useState({});
  // Estado para controlar el modal de login
  const [showLogin, setShowLogin] = useState(false);
  // Estado para controlar si se está agregando al carrito
  const [agregandoCarrito, setAgregandoCarrito] = useState(false);
  
  // Estados para el modal de feedback
  const [feedbackModal, setFeedbackModal] = useState({
    visible: false,
    type: 'error',
    title: '',
    message: ''
  });
  

  // Obtener funciones del contexto del carrito y usuario
  const { agregarAlCarrito: addToCart, cargarProductosCarrito } = useCart();
  const { codUsu, mail } = useUser();
  // Efecto para cargar los productos combinables cuando el modal se abre
  useEffect(() => {
    if (visible && combo && combo.nroCombo) {
      loadCombinableProducts();
      // Reiniciar las cantidades cada vez que se abre el modal
      setCantidades({});
    }
  }, [visible, combo]);

  // Responsive: detectar si la pantalla web es pequeña para ampliar el ancho a 90%
  const [viewportWidth, setViewportWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 1000
  );
  const isSmallWeb = Platform.OS === 'web' && viewportWidth < 768;

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  // Función para incrementar la cantidad de un producto
  const incrementarCantidad = (plu) => {
    setCantidades(prevCantidades => {
      const nuevaCantidad = (prevCantidades[plu] || 0) + 1;
      // Obtener el producto para verificar el stock disponible
      const productoActual = productos.find(p => p.plu === plu);

      // Limitar la cantidad al stock disponible si existe
      if (productoActual && productoActual.stock) {
        return {
          ...prevCantidades,
          [plu]: Math.min(nuevaCantidad, productoActual.stock)
        };
      } else {
        return {
          ...prevCantidades,
          [plu]: nuevaCantidad
        };
      }
    });
  };

  // Función para decrementar la cantidad de un producto
  const decrementarCantidad = (plu) => {
    setCantidades(prevCantidades => {
      const nuevaCantidad = Math.max((prevCantidades[plu] || 0) - 1, 0);
      return {
        ...prevCantidades,
        [plu]: nuevaCantidad
      };
    });
  };

  // Función para manejar el cambio directo de cantidad
  const cambiarCantidad = (plu, valor) => {
    const numeroValor = parseInt(valor) || 0;

    setCantidades(prevCantidades => {
      // Obtener el producto para verificar el stock disponible
      const productoActual = productos.find(p => p.plu === plu);

      // Limitar la cantidad al stock disponible si existe
      if (productoActual && productoActual.stock) {
        return {
          ...prevCantidades,
          [plu]: Math.min(Math.max(numeroValor, 0), productoActual.stock)
        };
      } else {
        return {
          ...prevCantidades,
          [plu]: Math.max(numeroValor, 0)
        };
      }
    });
  };

  // Función para cargar los productos combinables desde la API
  const loadCombinableProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener la sucursal del localStorage
      const sucursalId = await Storage.getItem('sucursalId');
      const suc = sucursalId;

      // Construir la URL con los parámetros necesarios
      const apiUrl = `${BACKEND_URL}/rest/productos/combinables?nroCombo=${combo.nroCombo}&suc=${suc}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Error al cargar productos combinables: ${response.status}`);
      }

      const data = await response.json();

      // Verificar que la respuesta sea correcta
      if (data && data.dsResultado.productos && Array.isArray(data.dsResultado.productos)) {
        setProductos(data.dsResultado.productos);
      } else {
        setProductos([]);
      }
    } catch (err) {
      console.error('Error al cargar productos combinables:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para agregar todos los productos seleccionados al carrito
  const combinarProductos = async () => {
    try {
      // Verificar login requerido: codUsu y mail
      if (!codUsu || !mail) {
        setShowLogin(true);
        return;
      }

      setAgregandoCarrito(true);

      // Construir array de productos a agregar
      const productosParaAgregar = [];

      // Agregar los productos combinables seleccionados
      Object.entries(cantidades).forEach(([plu, cantidad]) => {
        if (cantidad > 0) {
          productosParaAgregar.push({ plu: parseInt(plu), cant: cantidad });
        }
      });

      // Llamar a la API para agregar múltiples productos
      const { mail: userMail, codUsu: userCodUsu } = { mail, codUsu };
      
      // Obtener la sucursal del localStorage
      const sucursalId = await Storage.getItem('sucursalId');
      const suc = sucursalId;

      const queryParams = new URLSearchParams({
        mail: userMail || '',
        codUsu: userCodUsu || '',
        suc: suc
      }).toString();

      const body = {
        ttCarrito: productosParaAgregar
      };

      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = { message: responseText };
      }


      // Verificar si hay un error en la respuesta (resultado: false)
      if (data && data.dsResultado && data.dsResultado.resultado && data.dsResultado.resultado.length > 0) {
        const resultado = data.dsResultado.resultado[0];
        
        if (resultado.resultado === false) {
          // Mostrar el modal con el mensaje de error del servidor
          setFeedbackModal({
            visible: true,
            type: 'error',
            title: 'Productos no combinables',
            message: resultado.mensaje || 'No se pueden mezclar estos productos en el carrito'
          });
          return;
        }
      }

      // Si llegamos aquí, los productos se agregaron exitosamente
      if (response.ok) {
        // Recargar el carrito desde el contexto
        await cargarProductosCarrito();

        // Mostrar mensaje de éxito
        if (Platform.OS !== 'web') {
          Alert.alert(
            '¡Combo agregado!',
            `Se agregaron ${productosParaAgregar.length} productos al carrito con descuento.`,
            [{ text: 'OK' }]
          );
        }

        // Resetear cantidades
        setCantidades({});

        // Cerrar el modal
        onClose();

        // Disparar evento para actualizar el carrito en otros componentes
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const event = new CustomEvent('actualizarCarrito');
          window.dispatchEvent(event);
        }
      } else {
        const errorMsg = (data && data.message) || `Error ${response.status}: No se pudo agregar el combo al carrito.`;
        console.error('Error al agregar combo:', errorMsg);
        
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Productos no combinables',
          message: errorMsg
        });
      }
    } catch (error) {
      console.error('Error al combinar productos:', error);
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Productos no combinables',
        message: 'No se pudo agregar el combo al carrito. Los productos pueden no ser compatibles.'
      });
    } finally {
      setAgregandoCarrito(false);
    }
  };

  // Si no hay combo o producto, no mostrar nada
  if (!combo || !producto) {
    return null;
  }

  // Cálculo global para el estado del botón COMBINAR
  const seleccionadosGlobal = Object.values(cantidades).reduce((acc, val) => acc + (Number(val) || 0), 0);
  const requeridosGlobal = Number(combo?.cancom) || 0;
  const listoParaCombo = requeridosGlobal > 0 ? seleccionadosGlobal >= requeridosGlobal : false;

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.centeredView}>
        <View style={[
          styles.modalView,
          Platform.OS === 'web' && isSmallWeb ? { width: '90%' } : null
        ]}>
          {/* Header del modal */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{combo.descom} combiná y aprovechá</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <MaterialCommunityIcons name="close" size={24} color={colores.blanco} />
            </TouchableOpacity>
          </View>

          {/* Lista de productos sugeridos para combinar */}
          <ScrollView style={styles.scrollContainer}>
            <Text style={styles.sectionTitle}>Productos sugeridos</Text>

            {/* Mensaje de error si hay */}
            {error && (
              <View style={styles.messageContainer}>
                <MaterialCommunityIcons name="alert-circle-outline" size={40} color={colores.rojo} />
                <Text style={styles.errorText}>{error}</Text>
                <TouchableOpacity style={styles.retryButton} onPress={loadCombinableProducts}>
                  <Text style={styles.retryButtonText}>Reintentar</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Indicador de carga */}
            {loading && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colores.rojo} />
                <Text style={styles.loadingText}> combinables...</Text>
              </View>
            )}

            {/* Mensaje si no hay productos */}
            {!loading && !error && productos.length === 0 && (
              <View style={styles.messageContainer}>
                <MaterialCommunityIcons name="information-outline" size={40} color={colores.grisOscuro} />
                <Text style={styles.infoText}>
                  No se encontraron productos combinables para esta promoción.
                </Text>
              </View>
            )}

            {/* Lista de productos combinables de la API */}
            {!loading && productos.length > 0 && (
              <>
                {/*<Text style={styles.infoText}>
                  Estos son los productos que puedes combinar para esta promoción.
                </Text>*/}

                {/* Información del combo */}
                {/*{productos.length > 0 && productos[0].combo && productos[0].combo.length > 0 && (
                  <View style={styles.comboInfoCard}>
                    <Text style={styles.comboDetailText}>
                      <Text style={{fontWeight: 'bold'}}>{productos[0].combo[0].descom}</Text>
                      {productos[0].combo[0].cancom > 0 && (
                        ` - Necesitas ${productos[0].combo[0].cancom} productos para aprovechar el precio especial`
                      )}
                    </Text>
                  </View>
                )}*/}

                {/* Lista de productos combinables */}
                {productos.map((producto, index) => {
                  // Obtener datos del combo específico del producto
                  const productoCombo = producto.combo && producto.combo.length > 0 ? producto.combo[0] : null;
                  // URL de la imagen, ya sea de foto o de la primera foto en el array fotos
                  const imagenUrl = producto.foto ||
                    (producto.fotos && producto.fotos.length > 0 ? producto.fotos[0].imagen : null);

                  return (
                    <View key={index} style={styles.productCard}>
                      {/* Imagen del producto */}
                      {imagenUrl ? (
                        <Image
                          source={{ uri: imagenUrl }}
                          style={styles.productImage}
                          resizeMode="contain"
                        />
                      ) : (
                        <View style={styles.placeholderImage}>
                          <MaterialCommunityIcons name="image-outline" size={50} color={colores.grisClaro} />
                        </View>
                      )}

                      {/* Información del producto */}
                      <View style={styles.productInfo}>
                        {/* Descripción/nombre del producto */}
                        <Text style={styles.productName} numberOfLines={2}>
                          {safeText(producto.descripcion) || safeText(producto.nombre) || 'Producto'}
                        </Text>

                        {/* Categoría/departamento */}
                        <Text style={styles.productCategory}>
                          {safeText(producto.desDepto)}{safeText(producto.desCat) ? ` - ${safeText(producto.desCat)}` : ''}
                        </Text>

                        {/* Precios */}
                        <View style={styles.pricesContainer}>
                          {/* Precio regular */}
                          {producto.precio && (
                            <Text style={styles.productPrice}>
                              $ {formatPrecio(producto.precio, true)}
                            </Text>
                          )}

                          {/* Precio en combo si está disponible */}
                          {productoCombo && productoCombo.precom && (
                            <View style={styles.priceTag}>
                              <Text style={styles.priceTagText}>En combo: $ {formatPrecio(productoCombo.precom, true)}</Text>
                            </View>
                          )}
                        </View>
                        {/* Control de cantidad */}
                        <View style={styles.cantidadContainer}>
                          <TouchableOpacity
                            style={[styles.cantidadButton, cantidades[producto.plu] <= 0 ? styles.cantidadButtonDisabled : null]}
                            onPress={() => decrementarCantidad(producto.plu)}
                            disabled={cantidades[producto.plu] <= 0}
                          >
                            <Text style={styles.cantidadButtonText}>-</Text>
                          </TouchableOpacity>

                          <TextInput
                            style={styles.cantidadInput}
                            value={String(cantidades[producto.plu] || 0)}
                            onChangeText={(text) => cambiarCantidad(producto.plu, text)}
                            keyboardType="numeric"
                            maxLength={3}
                          />

                          <TouchableOpacity
                            style={[styles.cantidadButton,
                            (producto.stock && cantidades[producto.plu] >= producto.stock) ? styles.cantidadButtonDisabled : null]}
                            onPress={() => incrementarCantidad(producto.plu)}
                            disabled={producto.stock && cantidades[producto.plu] >= producto.stock}
                          >
                            <Text style={styles.cantidadButtonText}>+</Text>
                          </TouchableOpacity>

                          {/* Botón para agregar al carrito 
                          {cantidades[producto.plu] > 0 && (
                            <TouchableOpacity
                              style={styles.agregarButton}
                              onPress={() => agregarAlCarrito(producto, cantidades[producto.plu])}
                            >
                              <Text style={styles.agregarButtonText}>Agregar</Text>
                            </TouchableOpacity>
                          )} */}
                        </View>
                      </View>

                      {/* Cucarda/etiqueta de promoción si existe */}
                      {producto.cucarda && producto.cucarda.length > 0 && (
                        <View style={styles.cucardaContainer}>
                          <Image
                            source={{ uri: `${BACKEND_URL}${producto.cucarda[0].imagen}` }}
                            style={styles.cucardaImage}
                            resizeMode="contain"
                          />
                        </View>
                      )}
                    </View>
                  );
                })}
              </>
            )}
          </ScrollView>

          {/* Acción: info + botón (solo botón clickeable) */}
          <View style={[styles.actionButton, listoParaCombo ? styles.actionButtonEnabled : styles.actionButtonDisabled]}>
            {/* Descripción del combo */}
            <View>
              <Text style={styles.comboDescription}>
                Combiná este producto con otros y obtené un descuento.{combo.cancom > 1 ? ` Necesitas ${combo.cancom} productos.` : ''}
              </Text>
              {/* Progreso de selección para el combo */}
              {combo.cancom > 0 && (
                (() => {
                  const seleccionados = Object.values(cantidades).reduce((acc, val) => acc + (Number(val) || 0), 0);
                  const requeridos = Number(combo.cancom) || 0;
                  const faltan = Math.max(0, requeridos - seleccionados);
                  const pct = Math.max(0, Math.min(100, (requeridos > 0 ? (seleccionados / requeridos) * 100 : 0)));
                  return (
                    <View style={styles.progressContainer}>
                      <View style={styles.progressBarBg}>
                        <View style={[styles.progressBarFill, { width: `${pct}%` }]} />
                      </View>
                      <Text style={[styles.progressText, faltan === 0 && styles.statusReady]}>
                        {seleccionados} de {requeridos} seleccionados · {faltan > 0 ? `Faltan ${faltan}` : '¡Listo!'}
                      </Text>
                    </View>
                  );
                })()
              )}
            </View>
            {/* Botón CTA */}
            <TouchableOpacity
              style={[styles.actionCtaButton, listoParaCombo ? styles.actionCtaEnabled : styles.actionCtaDisabled]}
              onPress={combinarProductos}
              disabled={!listoParaCombo || agregandoCarrito}
            >
              {agregandoCarrito ? (
                <ActivityIndicator size="small" color={colores.blanco} />
              ) : (
                <Text style={[styles.actionButtonText, !listoParaCombo && styles.actionButtonTextDisabled]}>COMBINAR</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
      
      {/* Modal de Login cuando el usuario no está autenticado */}
      <Login visible={showLogin} onClose={() => setShowLogin(false)} />
      
      {/* Modal de feedback para errores */}
      <FeedbackModal
        visible={feedbackModal.visible}
        type={feedbackModal.type}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText="Entendido"
        onClose={() => setFeedbackModal({ ...feedbackModal, visible: false })}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalView: {
    backgroundColor: colores.blanco,
    width: Platform.OS === 'web' ? '60%' : '90%',
    maxWidth: 600,
    maxHeight: '90%',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
      }
    ),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    backgroundColor: colores.rojo,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
  },
  modalTitle: {
    color: colores.blanco,
    fontSize: 18,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 5,
  },

  comboTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  comboDescription: {
    fontSize: 14,
    color: colores.textoSecundario,
  },
  progressContainer: {
    marginTop: 8,
  },
  progressBarBg: {
    width: '100%',
    height: 8,
    backgroundColor: '#EAEAEA',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: colores.rojo,
  },
  progressText: {
    marginTop: 6,
    fontSize: 12,
    color: colores.textoSecundario,
  },
  statusReady: {
    color: colores.verde,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 10,
    paddingVertical: 5,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
  },
  currentProductContainer: {
  },
  scrollContainer: {
    flex: 1,
  },
  productCard: {
    flexDirection: 'row',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    marginHorizontal: 15,
  },
  productImage: {
    width: 80,
    height: 80,
    marginRight: 10,
  },
  placeholderImage: {
    width: 80,
    height: 80,
    backgroundColor: '#f9f9f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  productInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  productName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 5,
    textTransform: 'uppercase',
  },
  productCategory: {
    fontSize: 12,
    color: colores.grisOscuro,
    marginBottom: 5,
  },
  pricesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginVertical: 5,
  },
  comboInfoCard: {
    backgroundColor: colores.grisClaro,
    padding: 10,
    marginHorizontal: 15,
    marginBottom: 15,
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: colores.rojo,
  },
  comboDetailText: {
    fontSize: 14,
    color: colores.textoSecundario,
  },
  stockText: {
    fontSize: 12,
    color: colores.verde,
    marginTop: 5,
  },
  cucardaContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 40,
    height: 40,
    zIndex: 2,
  },
  cucardaImage: {
    width: '100%',
    height: '100%',
  },
  productPrice: {
    fontSize: 16,
    color: colores.rojo,
    fontWeight: 'bold',
    flexShrink: 1,
  },
  priceTag: {
    backgroundColor: colores.rojo,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginTop: 5,
    alignSelf: 'flex-start',
  },
  priceTagText: {
    color: colores.blanco,
    fontSize: 12,
    fontWeight: 'bold',
  },
  infoText: {
    fontSize: 12,
    color: colores.textoSecundario,
    marginBottom: 10,
    paddingHorizontal: 15,
  },
  errorText: {
    fontSize: 14,
    color: colores.rojo,
    textAlign: 'center',
    marginVertical: 10,
  },
  loadingText: {
    fontSize: 14,
    color: colores.textoSecundario,
    textAlign: 'center',
    marginTop: 10,
  },
  loadingContainer: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageContainer: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryButton: {
    backgroundColor: colores.rojo,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
    marginTop: 15,
  },
  retryButtonText: {
    color: colores.blanco,
    fontWeight: 'bold',
  },
  cantidadContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 5,
  },
  cantidadButton: {
    backgroundColor: colores.rojo,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  cantidadButtonDisabled: {
    backgroundColor: colores.grisClaro,
  },
  cantidadButtonText: {
    color: colores.blanco,
    fontSize: 18,
    fontWeight: 'bold',
  },
  cantidadInput: {
    width: 40,
    height: 30,
    borderWidth: 1,
    borderColor: colores.grisClaro,
    textAlign: 'center',
    marginHorizontal: 5,
    borderRadius: 4,
    fontSize: 14,
  },
  agregarButton: {
    backgroundColor: colores.verde,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginLeft: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  agregarButtonText: {
    color: colores.blanco,
    fontSize: 12,
    fontWeight: 'bold',
  },
  actionButton: {
    borderTopWidth: 1,
    borderTopColor: colores.grisClaro,
    padding: 15,
    alignItems: 'stretch',
    marginTop: 10,
  },
  actionButtonEnabled: {
    // contenedor normal
  },
  actionButtonDisabled: {
    // contenedor normal cuando no está listo
  },
  actionButtonText: {
    color: colores.blanco,
    fontWeight: 'bold',
  },
  actionButtonTextDisabled: {
    color: colores.grisOscuro,
  },
  // Nuevo: botón CTA separado (gris cuando no se puede, rojo cuando se puede)
  actionCtaButton: {
    alignSelf: 'flex-end',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    marginHorizontal: 15,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? { cursor: 'pointer' }
      : null
    ),
  },
  actionCtaEnabled: {
    backgroundColor: colores.rojo,
  },
  actionCtaDisabled: {
    backgroundColor: colores.grisClaro,
  },
});

export default Combinables;
